<template>
    <div class="page">
        <v-container>
            <v-card :flat="phoneOnly" class="px-lg-3 py-1" :class="{'transparent': phoneOnly, 'soft-shadow': pcOnly}">
                <v-card-title>Working Hours</v-card-title>
                <v-card-subtitle>Enter your fixed working hours (Please notify us before changing your working hours)</v-card-subtitle>
                
                <v-alert v-if="pendingRequest" dense type="info" class="mx-3 mt-2">
                    You have a pending working hours request. You cannot update your schedule until it is processed.
                </v-alert>
                <v-card-text>
                    <v-row style="max-width: 1100px">
                        <template v-for="(entry, i) in timeEntries">
                            <v-col cols="5" :key="'1-' + i" :class="{'px-1': phoneOnly}">
                                <v-select v-model="timeEntries[i]['day']" :dense="phoneOnly" :items="daysItems" :disabled="loading || saving || !!pendingRequest" outlined label="Day" placeholder="Choose Class Day" hide-details></v-select>
                            </v-col>
                            <v-col cols="3" :key="'2-' + i" :class="{'px-1': phoneOnly}">
                                <time-input v-model="timeEntries[i]['start_time']" :dense="phoneOnly" :hide-icon="phoneOnly" :disabled="(!timeEntries[i]['day'] && timeEntries[i]['day'] !== 0) || loading || saving || !!pendingRequest" outlined label="From" placeholder="From Time" /> <!-- :max="timeEntries[i]['end_time']" -->
                            </v-col>
                            <v-col cols="3" :key="'3-' + i" :class="{'px-1': phoneOnly}">
                                <time-input v-model="timeEntries[i]['end_time']" :dense="phoneOnly" :hide-icon="phoneOnly" :disabled="!timeEntries[i]['start_time'] || loading || saving || !!pendingRequest" outlined label="To" placeholder="To Time" /> <!-- :min="timeEntries[i]['start_time']" -->
                            </v-col>
                            <v-col cols="1" :key="'4-' + i" class="d-flex justify-start align-center" :class="{'px-0': phoneOnly}">
                                <v-btn v-if="i > 0 && !pendingRequest" color="error" icon @click="removeTimeEntry(i)">
                                    <v-icon size="20">mdi-close</v-icon>
                                </v-btn>
                            </v-col>
                        </template>
                        <v-col cols="12" class="py-0" v-if="!pendingRequest">
                            <v-btn color="primary" dense small text @click="addTimeEntry">
                                <v-icon left>mdi-plus</v-icon>
                                Additional Time Entry
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions class="pa-4">
                    <v-btn :loading="saving" :disabled="saving || !readyTimeEntries.length || !!pendingRequest" depressed color="primary" class="px-3" @click="reasonDialog = true">
                        <v-icon size="20" left>mdi-content-save-edit</v-icon>
                        <span>Send Request</span>
                    </v-btn>
                </v-card-actions>
            </v-card>

            <!-- Pending Request Card -->
            <v-card v-if="pendingRequest" :flat="phoneOnly" class="px-lg-3 py-1 mt-6" :class="{'transparent': phoneOnly, 'soft-shadow': pcOnly}">
                <v-card-title>Pending Request</v-card-title>
                <v-card-subtitle>You requested the following working hours on {{ pendingRequest.created_at.substring(0, 10) }}</v-card-subtitle>
                <v-card-text>
                    <v-row v-for="(entry, i) in pendingRequest.schedule" :key="i" class="mb-2 align-center">
                        <v-col cols="4" class="py-1">
                            <v-chip label outlined color="primary" class="w-100 justify-center">
                                {{ daysItems.find(d => d.value == entry.day) ? daysItems.find(d => d.value == entry.day).text : 'Unknown' }}
                            </v-chip>
                        </v-col>
                        <v-col cols="8" class="py-1">
                            <strong>{{ entry.start_time }}</strong> <span class="mx-2">to</span> <strong>{{ entry.end_time }}</strong>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-text v-if="pendingRequest.reason" class="pt-0">
                    <div class="text-subtitle-2 mb-1">Reason:</div>
                    <div class="grey--text text--darken-1">{{ pendingRequest.reason }}</div>
                </v-card-text>
                <v-card-actions class="pa-4">
                    <v-btn text color="error" class="px-3" @click="cancelRequest">
                        <v-icon size="20" left>mdi-cancel</v-icon>
                        <span>Cancel Request</span>
                    </v-btn>
                </v-card-actions>
            </v-card>

            <v-dialog v-model="reasonDialog" max-width="500" persistent>
                <v-card>
                    <v-card-title>Reason for change</v-card-title>
                    <v-card-text>
                        <div class="mb-3 grey--text text--darken-1">Please explain why you need to update your working hours.</div>
                        <v-textarea v-model="reason" outlined label="Reason" placeholder="e.g. I have a new university schedule starting next week" rows="3" counter="500" maxlength="500" hide-details="auto" :rules="[v => !!v || 'Reason is required', v => (v && v.length >= 5) || 'Reason must be at least 5 characters']"></v-textarea>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn text color="muted" @click="reasonDialog = false; reason = ''">Cancel</v-btn>
                        <v-btn depressed color="primary" :loading="saving" :disabled="saving || !reason || reason.length < 5" @click="saveChanges">Submit Request</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <v-dialog v-model="cancelDialog" max-width="350">
                <v-card>
                    <v-card-title>Cancel Request</v-card-title>
                    <v-card-text>Are you sure you want to cancel your pending working hours request?</v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn text color="muted" @click="cancelDialog = false">Close</v-btn>
                        <v-btn text color="error" :loading="canceling" :disabled="canceling" @click="confirmCancelRequest">Confirm</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-container>
    </div>
</template>

<script>
// @ is an alias to /src

import {isEqual} from 'lodash'

import GlobalComputed from '@/helpers/global-computed'
import GlobalMethods from '@/helpers/global-methods'

import TimeInput from '@/components/custom/time-input'
export default {
    name: "Course",
    components: {
        TimeInput
    },
    data(){
        return {
            loading: false,
            saving: false,
            pendingRequest: null,
            cancelDialog: false,
            canceling: false,
            reason: '',
            reasonDialog: false,

            timeEntries: [
                {day: '', start_time: '', end_time: ''},
                {day: '', start_time: '', end_time: ''},
                {day: '', start_time: '', end_time: ''},
            ],

            daysItems: [
                {text: 'Saturday', value: 6},
                {text: 'Sunday', value: 0},
                {text: 'Monday', value: 1},
                {text: 'Tuesday', value: 2},
                {text: 'Wednesday', value: 3},
                {text: 'Thursday', value: 4},
                {text: 'Friday', value: 5},
            ],
        }
    },
    computed: {
        readyTimeEntries(){
            return this.timeEntries.filter(time=>!!time.start_time && !!time.end_time)
        },
        ...GlobalComputed
    },
    watch: {
        loading(loading){
            this.setLinearLoading(loading);
        },
        saving(loading){
            this.setLinearLoading(loading);
        },
    },
    methods: {
        loadData(){
            if (!this.loading){
                this.loading = true;
                this.$store.dispatch('fastGetAction', {url: '/teacher-schedule'}).then(response=>{
                    this.loading = false;
                    const data = response.data.data;
                    this.pendingRequest = response.data.pending_request;
                    this.sync(data);
                }).catch(()=>{
                    this.loading = false;
                    this.showMsg('Error occurred during fetching data', 'error');
                })
            }
        },
        saveChanges(){
            if (!this.saving){
                this.saving = true;
                this.$store.dispatch('fastPutAction', {url: '/teacher-schedule', data: {schedule: this.readyTimeEntries, reason: this.reason}}).then(response=>{
                    this.saving = false;
                    const data = response.data.data;
                    this.pendingRequest = response.data.pending_request;
                    this.sync(data);
                    this.reason = '';
                    this.reasonDialog = false;
                    this.showMsg('Request Sent Successfully', 'success');
                }).catch(error=>{
                    this.saving = false;
                    if (error.response && error.response.status === 422){
                        // coming errors from backend
                        let errors = error.response.data.errors;
                        this.showMsg(errors[Object.keys(errors)[0]][0]);
                    }else{
                        this.showMsg('Error occurred during saving your changes', 'error');
                    }
                })
            }
        },

        cancelRequest() {
            this.cancelDialog = true;
        },
        confirmCancelRequest() {
            this.canceling = true;
            this.$store.dispatch('fastDeleteAction', {url: '/teacher-schedule/request'}).then(response=>{
                this.canceling = false;
                this.cancelDialog = false;
                const data = response.data.data;
                this.pendingRequest = response.data.pending_request;
                this.sync(data);
                this.showMsg('Request Canceled Successfully', 'success');
            }).catch(()=>{
                this.canceling = false;
                this.showMsg('Error occurred during cancelling your request', 'error');
            });
        },

        validate(){
            const data = this.data;
            if (!data.class_duration || !data.weekly_classes || !data.start_day){
                this.showMsg('Required inputs cannot be empty', 'error');
                return false;
            }else if (data.preferred_days.length < data.weekly_classes){
                this.showMsg(`"Preferred Days" must be equal or more than "Classes/ Week"`, 'error');
                return false;
            }
            return true;
        },
        addTimeEntry(){
            this.timeEntries.push({day: '', start_time: '', end_time: ''});
        },
        removeTimeEntry(i){
            this.timeEntries.splice(i, 1);
        },
        sync(data){
            if (data && data.length){
                this.timeEntries = data;
            }
        },
        ...GlobalMethods
    },
    mounted(){
        this.loadData();
    }
};
</script>
<style lang="scss" scoped>

</style>
